import { inject, Injectable } from '@angular/core';

import { Action, createSelector, Selector, State } from '@ngxs/store';

import { CommonAPI } from '@app/core/api/common.service';
import { BaseCurrencies, Currency, CurrencyRates, InvoiceCurrency } from '@app/core/models/customer.models';
import { UserGeneralSettings, UserSetting } from '@app/core/models/user.models';
import { patch } from '@ngxs/store/operators';
import { Observable, of, tap } from 'rxjs';
import { SetUserSettings } from '../user/user-state.actions';
import { UserState } from '../user/user.state';
import { GetCustomerCurrencyRate, SetUserCurrency } from './currency-state.actions';
import { CURRENCY_STATE_TOKEN, CurrencyStateModel } from './currency-state.models';

@Injectable()
@State<CurrencyStateModel>({
  name: CURRENCY_STATE_TOKEN,
  defaults: { customerCurrency: 'EUR', subscriptionCurrency: 'EUR', userCurrency: 'EUR', rates: {} as CurrencyRates },
})
export class CurrencyState {
  private readonly ca = inject(CommonAPI);

  @Selector([UserState.setting(UserSetting.General), CurrencyState])
  static userCurrency(general: UserGeneralSettings, state: CurrencyStateModel): Currency {
    return general?.currency || state.userCurrency;
  }

  @Selector()
  static rates({ rates }: CurrencyStateModel): CurrencyRates {
    return rates;
  }

  @Selector()
  static subscriptionCurrency({ subscriptionCurrency }: CurrencyStateModel): InvoiceCurrency {
    return subscriptionCurrency || 'EUR';
  }

  @Selector()
  static customerCurrency({ customerCurrency }: CurrencyStateModel): Currency {
    return customerCurrency;
  }

  static rateFor(valueCurrency: Currency) {
    return createSelector(
      [CurrencyState.userCurrency, CurrencyState.rates],
      (userCurrency: Currency, rates: CurrencyRates): number => {
        return rates[`${valueCurrency}-${userCurrency}`];
      },
    );
  }

  @Action(SetUserCurrency)
  SetUserCurrency({ setState, dispatch, getState }, { value, persist }: SetUserCurrency): Observable<unknown> {
    const { userCurrency } = getState();
    setState(patch({ userCurrency: value }));

    if (persist) {
      if (userCurrency !== value) {
        return dispatch(new SetUserSettings({ [UserSetting.General]: { currency: value } }));
      }
    }

    return of(void 0);
  }

  @Action(GetCustomerCurrencyRate)
  getCustomerCurrencyRate({ getState, setState }): Observable<unknown> {
    const { userCurrency } = getState();
    const currencies = [...BaseCurrencies, userCurrency];

    return this.ca.getCurrencyRate([...new Set(currencies)]).pipe(tap((rates) => setState(patch({ rates }))));
  }
}
